import { useRef } from "react";

import type { DashboardWidget } from "./types";

/**
 * Decide se o alarme de parada está ativo: a máquina está parada há pelo menos
 * `afterSeconds`. `stoppedSince` é o instante (ms) em que a parada começou
 * (`null` = não está parada). Função pura para testar sem montar componente.
 */
export function stopAlarmActive(
  stoppedSince: number | null,
  now: number,
  afterSeconds: number,
): boolean {
  if (stoppedSince === null) return false;
  return now - stoppedSince >= Math.max(0, afterSeconds) * 1000;
}

/**
 * Alarme de PARADA por widget (config `settings.stopAlarm`): vira `true` quando
 * a máquina está parada (`stopped`) e fresca (`fresh`) há mais que a tolerância.
 * Guarda o início da parada num ref que persiste entre os refreshes (o card não
 * remonta); zera ao voltar a rodar, ficar velho ou o alarme ser desligado.
 *
 * Mutar o ref durante o render é deliberado e seguro aqui: o valor derivado só
 * depende de `stoppedSince` + relógio, sem efeito colateral externo.
 */
export function useStopAlarm(opts: {
  enabled: boolean;
  stopped: boolean;
  fresh: boolean;
  afterMinutes: number;
  now?: number;
}): boolean {
  const sinceRef = useRef<number | null>(null);
  const now = opts.now ?? Date.now();

  if (!opts.enabled || !opts.fresh || !opts.stopped) {
    sinceRef.current = null;
    return false;
  }
  if (sinceRef.current === null) sinceRef.current = now;
  return stopAlarmActive(sinceRef.current, now, opts.afterMinutes * 60);
}

/** Lê a config de alarme de parada do widget (desligada por padrão). */
export function resolveStopAlarm(widget: DashboardWidget): {
  enabled: boolean;
  afterMinutes: number;
} {
  const cfg = widget.settings.stopAlarm;
  return {
    enabled: cfg?.enabled ?? false,
    afterMinutes: Math.max(0, cfg?.afterMinutes ?? 0),
  };
}
